import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLogoFrame } from '../../lib/useLogoFrame';
import { UploadCloudIcon } from '../icons';
import {
  Dropzone,
  DropIcon,
  DropText,
  Preview,
  PreviewImg,
  HiddenInput,
  PreviewWrap,
  Reupload,
  ReuploadLabel,
} from './LogoUploadArea.styles';

export interface LogoUploadAreaProps {
  /** Current logo data-URL (after upload), or null to show the dropzone. */
  value: string | null;
  /** `<input type="file">` change handler from the store (gallery upload). */
  onSelectFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant: 'desktop' | 'mobile';
}

/**
 * Logo upload area: a dashed "click or drop" dropzone that becomes a preview once
 * a file is chosen. The dropzone, the preview, and the desktop "Upload another
 * logo" link are all labels for one hidden input (configured per the area
 * convention — explicit image MIMEs, no `capture`); drop reuses the same handler.
 */
export const LogoUploadArea = ({ value, onSelectFile, variant }: LogoUploadAreaProps): React.ReactElement => {
  const { t } = useTranslation();
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  // The plate tone reacts to the uploaded logo's luminance (contrast backing).
  const frame = useLogoFrame(value);

  const input = (
    <HiddenInput id={inputId} type="file" accept="image/jpeg,image/png,image/svg+xml" onChange={onSelectFile} />
  );

  if (value) {
    return (
      <PreviewWrap>
        <Preview $variant={variant} $frame={frame} htmlFor={inputId}>
          <PreviewImg src={value} alt={t('Uploaded logo')} />
        </Preview>
        {variant === 'desktop' ? (
          <Reupload htmlFor={inputId}>
            <UploadCloudIcon size={16} />
            <ReuploadLabel text="Upload another logo" tag="span" />
          </Reupload>
        ) : null}
        {input}
      </PreviewWrap>
    );
  }

  return (
    <Dropzone
      $variant={variant}
      $dragging={dragging}
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;
        // Reuse the store's input-change handler — it only reads `event.target.files`.
        onSelectFile({ target: { files } } as unknown as React.ChangeEvent<HTMLInputElement>);
      }}
    >
      <DropIcon>
        <UploadCloudIcon size={24} />
      </DropIcon>
      <DropText text="Click or drop to upload your logo" tag="span" />
      {input}
    </Dropzone>
  );
};

export default LogoUploadArea;